import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const SmsPolicyPage = () => {
  const lastUpdated = 'December 30, 2025';

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Header */}
      <section className="pt-20 pb-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            SMS Messaging Terms & Opt-In Policy
          </h1>
          <p className="text-gray-600">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-gray max-w-none">

            {/* Overview */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Overview</h2>
              <p className="text-gray-700 leading-relaxed">
                SuddyFlow and businesses using the SuddyFlow platform ("Service Providers") may send SMS text 
                messages to customers who have provided consent. This policy explains how consent is obtained, 
                what messages you may receive, and how to opt out.
              </p>
            </div>

            {/* How Consent is Obtained */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">How Consent is Obtained</h2>
              <p className="text-gray-700 leading-relaxed">
                You may receive SMS messages from a Service Provider using SuddyFlow if you:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li>Provide your phone number when requesting a quote or service</li>
                <li>Submit your phone number through a contact form or website</li>
                <li>Verbally provide your phone number to a Service Provider</li>
                <li>Text a Service Provider first to inquire about services</li>
                <li>Check a box or sign an agreement consenting to receive SMS messages</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                By providing your phone number through any of these methods, you expressly consent to receive 
                SMS messages related to your service request, appointment, invoice, or account.
              </p>
            </div>

            {/* Types of Messages */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Types of Messages</h2>
              <p className="text-gray-700 leading-relaxed">
                You may receive SMS messages related to:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li>Appointment confirmations and reminders</li>
                <li>Quote and estimate notifications</li>
                <li>Invoice and payment reminders</li>
                <li>Service updates and notifications</li>
                <li>Responses to your inquiries</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Message frequency varies</strong> based on your interactions with the Service Provider. 
                You will only receive messages relevant to services you have requested or inquired about.
              </p>
            </div>

            {/* Opt-Out Instructions */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Opt-Out Instructions</h2>
              <p className="text-gray-700 leading-relaxed">
                You can stop receiving SMS messages at any time by:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li>Replying <strong>STOP</strong> to any message</li>
                <li>Contacting the Service Provider directly</li>
                <li>Emailing <a href="mailto:support@suddyflow.com" className="text-blue-600 hover:underline">support@suddyflow.com</a></li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                After opting out, you will receive one confirmation message. No further messages will be sent 
                unless you re-consent.
              </p>
            </div>

            {/* Opt Back In */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Opt Back In</h2>
              <p className="text-gray-700 leading-relaxed">
                To opt back in after opting out, reply <strong>START</strong> to any previous message thread 
                or contact the Service Provider directly.
              </p>
            </div>

            {/* Cost and Carrier Information */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Cost and Carrier Information</h2>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li><strong>Message and data rates may apply</strong> depending on your mobile carrier and plan</li>
                <li>SuddyFlow and Service Providers are not responsible for carrier fees</li>
                <li>SMS messages are sent via standard text messaging protocols</li>
              </ul>
            </div>

            {/* Supported Carriers */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Supported Carriers</h2>
              <p className="text-gray-700 leading-relaxed">
                SMS messages are supported on all major U.S. carriers including AT&T, Verizon, T-Mobile, 
                US Cellular, and most regional carriers. Carrier support may vary for some features.
              </p>
            </div>

            {/* Privacy */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Phone numbers are used solely for the purposes described above and are not sold or shared 
                with third parties for marketing purposes. See our{' '}
                <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link> for 
                more information about how we handle your data.
              </p>
            </div>

            {/* Help */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Help</h2>
              <p className="text-gray-700 leading-relaxed">
                For help or questions about SMS messaging:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                <li>Reply <strong>HELP</strong> to any message</li>
                <li>Email: <a href="mailto:support@suddyflow.com" className="text-blue-600 hover:underline">support@suddyflow.com</a></li>
                <li>Website: <a href="https://suddyflow.com" className="text-blue-600 hover:underline">suddyflow.com</a></li>
              </ul>
            </div>

            {/* Agreement */}
            <div className="mb-10">
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <p className="text-gray-700 leading-relaxed">
                  By providing your phone number to a Service Provider using SuddyFlow, you agree to these 
                  SMS Messaging Terms. This consent is not a condition of purchase.
                </p>
              </div>
            </div>

            {/* Contact */}
            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions about this SMS Policy, please contact us at:
              </p>
              <div className="mt-4 bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700">
                  <strong>Suddy Buckets LLC</strong><br />
                  Email: <a href="mailto:support@suddyflow.com" className="text-blue-600 hover:underline">support@suddyflow.com</a><br />
                  Website: <a href="https://suddyflow.com" className="text-blue-600 hover:underline">suddyflow.com</a>
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SmsPolicyPage;